/**
 * CANOA — GSAP animation layer.
 *
 * All site motion lives here per the frontend stack rules: GSAP owns animation,
 * Alpine owns UI state, CSS owns static styling and simple hovers.
 *
 * Load order (theme.liquid, before </body>): GSAP → ScrollTrigger → this file → Alpine.
 *
 * NOT animated here, by design:
 *   - Header: always fixed at the top (theme setting `enable_sticky_header: always`
 *     + the inline height IIFE in theme.liquid). No hide/show, no GSAP.
 *   - Product grid: this is an institutional site — no products/collections.
 *
 * ── Section entrances ───────────────────────────────────────────────────────
 * Per-section opt-in via a `data-animate` attribute. One system per element:
 * if an element gets `data-animate`, don't also leave a CSS-keyframe or
 * IntersectionObserver reveal on it.
 *
 *   data-animate="<type>"   fade | fade-up | fade-down | fade-left | fade-right
 *                           | scale | zoom | blur-rise | lines | typewriter | parallax
 *
 *   Optional modifiers (sensible defaults per type):
 *     data-animate-duration="0.8"     seconds
 *     data-animate-delay="0"          seconds
 *     data-animate-stagger="0.09"     seconds between lines (type="lines")
 *     data-animate-distance="40"      px of travel (slide / rise types)
 *     data-animate-start="top 85%"    ScrollTrigger start
 *     data-animate-once="false"       replay every time it scrolls into view (default: play once)
 *     data-animate-speed="55"         characters/sec (type="typewriter")
 *
 * Reduced motion (prefers-reduced-motion: reduce): every element is shown in its
 * final state with no motion; text treatments render their full text immediately.
 */

// Safety net: base.css pre-hides every [data-animate] element (visibility:hidden)
// while JS is on, and only THIS file reveals them. If GSAP or ScrollTrigger failed
// to load (a slow/blocked network, a CDN hiccup), referencing `gsap` below would
// throw and the whole engine would never run — leaving that content invisible for
// good. So before anything else: if the libraries aren't here, just reveal the
// content (no motion) and bail. Content visible always beats motion.
if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
  document.querySelectorAll('[data-animate]').forEach((el) => {
    el.style.visibility = 'visible';
  });
} else {
  gsap.registerPlugin(ScrollTrigger);
  runAnimations();
}

function runAnimations() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Per-type defaults: [duration, ease]
  const DEFAULTS = {
    'fade':       [0.8, 'power2.out'],
    'fade-up':    [0.8, 'power3.out'],
    'fade-down':  [0.8, 'power3.out'],
    'fade-left':  [0.8, 'power3.out'],
    'fade-right': [0.8, 'power3.out'],
    'scale':      [0.8, 'power2.out'],
    'zoom':       [1.0, 'power2.out'],
    'blur-rise':  [0.9, 'power2.out'],
    'lines':      [0.9, 'power3.out'],
    'typewriter': [0,   'none'],
    'parallax':   [0,   'none'],
  };

  // ScrollTriggers/tweens created by the engine, so we can rebuild on resize.
  const created = [];
  // Elements split into lines (need re-splitting when width changes).
  const lineEls = [];
  // Every play-on-enter reveal, so the resume sweep can force-finish any the
  // browser's suspend/restore cycle left stranded (see revealSweep below).
  const reveals = [];

  function num(el, attr, fallback) {
    const v = parseFloat(el.getAttribute(attr));
    return Number.isFinite(v) ? v : fallback;
  }

  function opts(el, type) {
    const [dur, ease] = DEFAULTS[type] || DEFAULTS['fade'];
    return {
      duration: num(el, 'data-animate-duration', dur),
      delay: num(el, 'data-animate-delay', 0),
      distance: num(el, 'data-animate-distance', 40),
      stagger: num(el, 'data-animate-stagger', 0.09),
      speed: num(el, 'data-animate-speed', 55),
      start: el.getAttribute('data-animate-start') || 'top 85%',
      once: el.getAttribute('data-animate-once') !== 'false',
      ease,
    };
  }

  // Build a ScrollTrigger that plays `tween` when the element enters.
  function onEnter(el, o, tween) {
    tween.pause();
    const st = ScrollTrigger.create({
      trigger: el,
      start: o.start,
      once: o.once,
      onEnter: () => tween.play(),
      // When repeating, reset + replay each time it re-enters.
      onEnterBack: o.once ? undefined : () => tween.play(),
      onLeaveBack: o.once ? undefined : () => tween.pause(0),
    });
    created.push(st, tween);
    reveals.push({ el, tween });
    return st;
  }

  // ── Transform/filter reveals ────────────────────────────────────────────
  function fromState(type, o) {
    switch (type) {
      case 'fade':       return { opacity: 0 };
      case 'fade-up':    return { opacity: 0, y: o.distance };
      case 'fade-down':  return { opacity: 0, y: -o.distance };
      case 'fade-left':  return { opacity: 0, x: -o.distance };
      case 'fade-right': return { opacity: 0, x: o.distance };
      case 'scale':      return { opacity: 0, scale: 0.92 };
      case 'zoom':       return { opacity: 0, scale: 1.1 };
      case 'blur-rise':  return { opacity: 0, y: o.distance * 0.6, filter: 'blur(8px)' };
      default:           return { opacity: 0 };
    }
  }

  function initReveal(el, type, o) {
    const from = fromState(type, o);
    gsap.set(el, from);
    const to = { opacity: 1, x: 0, y: 0, scale: 1, duration: o.duration, ease: o.ease, delay: o.delay };
    if ('filter' in from) to.filter = 'blur(0px)';
    onEnter(el, o, gsap.to(el, to));
  }

  // ── Parallax (scrubbed drift) ─────────────────────────────────────────────
  function initParallax(el, o) {
    const dist = o.distance || 40;
    const tween = gsap.fromTo(el, { y: dist }, { y: -dist, ease: 'none' });
    const st = ScrollTrigger.create({
      trigger: el,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      animation: tween,
    });
    created.push(st, tween);
  }

  // ── Line-by-line text ─────────────────────────────────────────────────────
  // Lightweight, dependency-free line splitter (no SplitText needed).
  function splitIntoLines(el) {
    if (!el.dataset.animHtml) el.dataset.animHtml = el.innerHTML;
    // Only safe on plain-text elements; fall back if there's nested markup.
    if (el.children.length > 0) return null;

    const text = el.textContent.replace(/\s+/g, ' ').trim();
    const words = text.split(' ');
    el.textContent = '';
    const wordSpans = words.map((w, i) => {
      const s = document.createElement('span');
      s.textContent = w;
      el.appendChild(s);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return s;
    });

    // Group words into lines by their vertical offset.
    const lines = [];
    let cur = null, curTop = null;
    wordSpans.forEach((s) => {
      const top = s.offsetTop;
      if (curTop === null || Math.abs(top - curTop) > 1) { cur = []; lines.push(cur); curTop = top; }
      cur.push(s.textContent);
    });

    // Rebuild as masked line wrappers (overflow-hidden outer, translatable inner).
    el.innerHTML = '';
    const inners = lines.map((wordsInLine) => {
      const outer = document.createElement('span');
      outer.className = 'anim-line';
      const inner = document.createElement('span');
      inner.className = 'anim-line__inner';
      inner.textContent = wordsInLine.join(' ');
      outer.appendChild(inner);
      el.appendChild(outer);
      return inner;
    });
    return inners;
  }

  function initLines(el, o) {
    const inners = splitIntoLines(el);
    if (!inners) { initReveal(el, 'blur-rise', o); return; } // graceful fallback
    gsap.set(inners, { yPercent: 110 });
    const tween = gsap.to(inners, {
      yPercent: 0,
      duration: o.duration,
      ease: o.ease,
      delay: o.delay,
      stagger: o.stagger,
    });
    onEnter(el, o, tween);
    lineEls.push(el);
  }

  // ── Typewriter ─────────────────────────────────────────────────────────────
  function initTypewriter(el, o) {
    if (!el.dataset.animHtml) el.dataset.animHtml = el.innerHTML;
    if (el.children.length > 0) { initReveal(el, 'fade', o); return; } // plain text only

    const full = el.textContent;
    el.textContent = '';
    el.classList.add('anim-typewriter');
    const text = document.createElement('span');
    const caret = document.createElement('span');
    caret.className = 'anim-caret';
    caret.setAttribute('aria-hidden', 'true');
    el.append(text, caret);

    const state = { n: 0 };
    const duration = full.length / Math.max(1, o.speed);
    const tween = gsap.to(state, {
      n: full.length,
      duration,
      ease: 'none',
      delay: o.delay,
      onUpdate: () => { text.textContent = full.slice(0, Math.round(state.n)); },
      onComplete: () => { text.textContent = full; },
    });
    // Blinking caret (GSAP owns the motion — no CSS keyframes).
    created.push(gsap.to(caret, { opacity: 0, duration: 0.5, repeat: -1, yoyo: true, ease: 'steps(1)' }));
    onEnter(el, o, tween);
  }

  // ── Dispatch ────────────────────────────────────────────────────────────────
  function animate(el) {
    const type = el.getAttribute('data-animate');
    if (!type) return;
    const o = opts(el, type);

    // Reveal from the JS-active pre-hide (.js [data-animate]{visibility:hidden})
    // now that the engine controls this element's state.
    gsap.set(el, { visibility: 'visible' });

    if (prefersReducedMotion) {
      // Show final state, no motion. Text treatments keep their original text.
      gsap.set(el, { opacity: 1, clearProps: 'transform,filter' });
      return;
    }

    switch (type) {
      case 'parallax':   initParallax(el, o); break;
      case 'lines':      initLines(el, o); break;
      case 'typewriter': initTypewriter(el, o); break;
      default:           initReveal(el, type, o); break;
    }
  }

  function initSectionEntrances() {
    document.querySelectorAll('[data-animate]').forEach(animate);
    ScrollTrigger.refresh();
  }

  // Re-split line elements after a width change (line breaks shift).
  //
  // ⚠️ Mobile-Safari hardening: iOS fires `resize` for far more than real window
  // resizes — collapsing toolbar, keyboard, and (critically) when a suspended tab
  // is restored, sometimes while the page is still hidden with stale dimensions.
  // The rebuild tears every reveal down to its hidden "before" state and relies
  // on fresh ScrollTriggers to re-reveal; doing that mid-restore computed garbage
  // positions and left the top-of-page text invisible after returning to the tab.
  // So: rebuild ONLY on a real width change, NEVER while hidden (defer to the
  // next `visibilitychange` → visible), and follow up with a reveal sweep.
  let resizeTimer;
  let lastWidth = window.innerWidth;
  let pendingRebuild = false;

  function rebuild() {
    created.forEach((c) => c.kill && c.kill());
    created.length = 0;
    lineEls.length = 0;
    reveals.length = 0;
    document.querySelectorAll('[data-animate]').forEach((el) => {
      if (el.dataset.animHtml) el.innerHTML = el.dataset.animHtml; // restore for clean re-split
    });
    initSectionEntrances();
    revealSweep(); // anything already at/above the viewport must come straight back
  }

  function onResize() {
    if (window.innerWidth === lastWidth) return; // height-only (iOS toolbar/keyboard/restore)
    lastWidth = window.innerWidth;
    if (!lineEls.length || prefersReducedMotion) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (document.hidden) { pendingRebuild = true; return; }
      rebuild();
    }, 200);
  }

  // Resume safety net. After a bfcache restore or a tab coming back to the
  // foreground, iOS can leave entrance tweens stranded: element scrolled past its
  // trigger but the tween never played (rAF frozen / triggers built on stale
  // layout) → content invisible. Sweep every registered reveal: if its element
  // is at or above the viewport's lower edge and its tween hasn't started, play
  // it now. Elements still below the fold keep their normal scroll-in entrance.
  function revealSweep() {
    if (document.hidden) return;
    ScrollTrigger.refresh();
    reveals.forEach(({ el, tween }) => {
      if (tween.progress() > 0 || !tween.paused()) return; // played / playing
      if (el.getBoundingClientRect().top < window.innerHeight * 0.95) tween.play();
    });
  }

  // iOS Safari can restore a suspended tab with BLANK composited layers — the
  // slideshows are prime victims (content-visibility:auto slides + sticky
  // pinning + scale transforms) — and with stale scroll-driven timelines (the
  // .slide__content opacity animation), all outside GSAP's reach: the content
  // is "there" but paints nothing until something forces a recomposite. So on
  // every return-to-visible, nudge each slideshow near the viewport: toggle
  // the theme's own `refreshing-timeline` attribute for a frame (slideshow.js
  // uses the same trick after loops — _slide.liquid sets animation:none while
  // it's on, forcing Safari to recalculate the view-timeline) and pulse an
  // inline transform on the section to force a fresh compositor layer.
  // Off-screen slideshows are left alone (their render cost stays deferred).
  function resumeRepaintNudge() {
    document.querySelectorAll('slideshow-component').forEach((c) => {
      const rect = c.getBoundingClientRect();
      if (rect.bottom < -window.innerHeight || rect.top > window.innerHeight * 2) return;
      c.setAttribute('refreshing-timeline', '');
      const section = c.closest('.shopify-section');
      if (section) section.style.transform = 'translateZ(0)';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          c.removeAttribute('refreshing-timeline');
          if (section) section.style.transform = '';
        });
      });
    });
  }

  function onVisible() {
    if (document.hidden) return;
    resumeRepaintNudge();
    if (pendingRebuild) {
      pendingRebuild = false;
      rebuild();
    } else {
      revealSweep();
    }
  }

  // ── Slideshow section reveals (homepage) ────────────────────────────────────
  // Owns the section-level motion that used to be CSS `animation-timeline: view()`.
  // Cost is FLAT regardless of how many slideshows the page has: ONE batched
  // observer per effect (not one trigger per section).
  //   · Aperture     — ScrollTrigger.batch opens a clip-path window on enter.
  //   · Sticky-stack — the same batched observer toggles `.is-covered`; the CSS
  //     transition (slideshow.liquid) eases scale/brightness as each case is
  //     covered. Native `position: sticky` still does the pinning.

  const CSS_EASE = { 'ease-out': 'power2.out', 'ease-in': 'power2.in', 'ease-in-out': 'power2.inOut', 'ease': 'power1.inOut', 'linear': 'none' };
  const APERTURE_FROM = {
    split_h: 'inset(46% 0 46% 0)', split_v: 'inset(0 46% 0 46%)', iris: 'circle(6% at 50% 50%)',
    wipe_up: 'inset(100% 0 0 0)', wipe_down: 'inset(0 0 100% 0)', wipe_left: 'inset(0 0 0 100%)', wipe_right: 'inset(0 100% 0 0)',
  };

  function initSlideshowReveals() {
    if (prefersReducedMotion) return; // static slides, no motion

    // --- Aperture: one batched observer, play-on-enter clip-path open.
    // Editor controls (global) drive it: duration/start/easing read from the
    // section's --canoa-ap-* vars; direction → start clip-path. ---
    const apSlides = gsap.utils.toArray('.slideshow-section--reveal-aperture slideshow-slides');
    if (apSlides.length) {
      const cs = getComputedStyle(apSlides[0].closest('.slideshow-section'));
      const dur = parseFloat(cs.getPropertyValue('--canoa-ap-dur')) || 0.9;
      const startPct = parseFloat(cs.getPropertyValue('--canoa-ap-start')) || 80;
      const ease = CSS_EASE[cs.getPropertyValue('--canoa-ap-ease').trim()] || 'power2.out';
      apSlides.forEach((slides) => {
        const dirClass = [...slides.closest('.slideshow-section').classList].find((c) => c.startsWith('slideshow-section--ap-'));
        const dir = dirClass ? dirClass.replace('slideshow-section--ap-', '') : 'split_h';
        gsap.set(slides, { clipPath: APERTURE_FROM[dir] || APERTURE_FROM.split_h });
      });
      ScrollTrigger.batch(apSlides, {
        start: `top ${startPct}%`,
        once: true,
        onEnter: (batch) => batch.forEach((slides) =>
          gsap.to(slides, { clipPath: 'inset(0 0 0 0)', duration: dur, ease })),
      });
    }

    // --- Sticky-stack recede: one batched observer toggles .is-covered ---
    // A case is "covered" once the NEXT stack section reaches the top (pins over
    // it). The last section is never covered → never recedes (handled for free).
    //
    // D62 — pause buried videos. Pinned-but-covered sections stay geometrically
    // in the viewport, so the browser keeps ALL their autoplay videos decoding
    // (verified: at page bottom every prior video section was still playing
    // under the stack). On iOS the pile of live decoders + composited sticky
    // layers overflows GPU memory and Safari drops/recreates layers mid-scroll —
    // the "cropped" flicker + chunky scrolling Laura saw, worst on direction
    // reversals (un-cover scale transitions land on top of the pressure). So:
    // covered → pause its videos; uncovered → resume ONLY the ones we paused
    // (flagged via dataset), never slide-sound's parked/gated videos (§28 owns
    // those — they're already paused, so they never get the flag).
    const stack = gsap.utils.toArray('.slideshow-section--reveal-stack');
    if (stack.length > 1) {
      const prevOf = (sec) => { const i = stack.indexOf(sec); return i > 0 ? stack[i - 1] : null; };
      const setCovered = (sec, covered) => {
        if (!sec) return;
        sec.classList.toggle('is-covered', covered);
        sec.querySelectorAll('video').forEach((v) => {
          // D76 — a slide-sound-owned video is NEVER resumed from here, even
          // when this pause flagged it: a bare play() would restart it outside
          // slide-sound's state machine (possibly unmuted at volume 0, pill
          // desynced). Its own cover sentinel re-enters via enable() instead.
          const soundOwned = !!v.closest('slideshow-slide')?.querySelector('slide-sound');
          clearTimeout(v._canoaResumeTimer);
          if (covered) {
            if (!v.paused) {
              v.dataset.canoaCoverPaused = '1';
              v.pause();
            }
          } else if (soundOwned) {
            delete v.dataset.canoaCoverPaused;
          } else if (v.dataset.canoaCoverPaused) {
            // Debounced resume (D63): at a reversal boundary the cover state
            // flips rapidly, and restarting a decoder mid-flick hitches iOS
            // scrolling. Resume only once the uncover has held for a beat.
            v._canoaResumeTimer = setTimeout(() => {
              if (!v.dataset.canoaCoverPaused) return;
              delete v.dataset.canoaCoverPaused;
              v.play().catch(() => {});
            }, 250);
          }
        });
      };
      ScrollTrigger.batch(stack, {
        start: 'top top',
        onEnter: (batch) => batch.forEach((s) => setCovered(prevOf(s), true)),
        onLeaveBack: (batch) => batch.forEach((s) => setCovered(prevOf(s), false)),
      });

      // D67 — mobile entrance. Sticky pinning is desktop-only now (the
      // touch-drag wobble over pinned sections survived every mitigation —
      // D62–D66 — so phones get normal flow), and each slideshow instead
      // arrives with a light rise+fade, once, on first approach. Tweens are
      // pre-created paused and registered in `reveals`, so the D54 resume
      // sweep rescues any section a suspend/restore would otherwise strand
      // hidden. The cover batch above still runs on mobile — is-covered has
      // no mobile styles anymore, but the buried-video pause (D62) is a
      // battery win in normal flow too.
      if (window.matchMedia('(max-width: 749px)').matches) {
        const entrances = new Map();
        stack.forEach((s) => {
          gsap.set(s, { opacity: 0, y: 28 });
          const tween = gsap.to(s, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', paused: true });
          entrances.set(s, tween);
          reveals.push({ el: s, tween });
        });
        ScrollTrigger.batch(stack, {
          start: 'top 85%',
          once: true,
          onEnter: (batch) => batch.forEach((s) => entrances.get(s)?.play()),
        });
      }
    }
  }

  function start() {
    initSectionEntrances();
    initSlideshowReveals();
    window.addEventListener('resize', onResize);
    // Sticky-section heights/positions settle once media finishes loading.
    window.addEventListener('load', () => ScrollTrigger.refresh());
    // Suspend/restore recovery (mobile Safari): see revealSweep above.
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', (e) => { if (e.persisted) { resumeRepaintNudge(); revealSweep(); } });
  }

  // Kick off once the DOM is parsed. Line splitting (type="lines") reads final
  // font metrics, so we PREFER to wait for webfonts — but `document.fonts.ready`
  // can hang on some mobile Safari builds with several @font-face declarations,
  // and `start()` is the ONLY thing that reveals [data-animate] content (base.css
  // pre-hides it with visibility:hidden). A promise that never resolves would
  // therefore leave the hero/heading text invisible forever. So race fonts.ready
  // against a short timeout and start on whichever fires first, once. Worst case
  // a line split happens a hair early with fallback metrics — content visible
  // always beats a perfect split.
  const FONTS_READY_TIMEOUT = 1500; // ms
  function boot() {
    let started = false;
    const runOnce = () => {
      if (started) return;
      started = true;
      start();
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(runOnce);
      setTimeout(runOnce, FONTS_READY_TIMEOUT);
    } else {
      runOnce();
    }
  }
  // Deferred scripts run before DOMContentLoaded, but guard for the already-fired
  // case too (e.g. if this ever loads late) so boot() can never be missed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
